/**
 * The match RNG.
 *
 * mulberry32: 32 bits of state, one multiply-xorshift round, no wall clock and
 * no `Math.random` anywhere. The state rides on the world object, is advanced
 * only inside `stepWorld`, and is part of the serialized world — so a replay of
 * the input log reproduces every "random" decision the match made: spawn
 * assignment, the zone centre, every barrage shell.
 *
 * That is the whole point. `Math.random()` in a step would make the seed
 * commitment published at lobby lock a decoration.
 */
import { dcos, dsin, TWO_PI } from "./math.js";
/**
 * FNV-1a over the seed string. Not cryptographic — its job is to spread an
 * arbitrary seed string across 32 bits, not to hide it. The lobby's published
 * commitment is a real hash computed server-side over the same seed.
 */
export function hashSeed(seed) {
    let h = 0x811c9dc5;
    for (let i = 0; i < seed.length; i++) {
        h ^= seed.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}
/** A short deterministic digest, used for world checksums and seed fingerprints. */
export function digest(text) {
    let a = 0x811c9dc5;
    let b = 0x01000193;
    let c = 0x9e3779b9;
    let d = 0x85ebca6b;
    for (let i = 0; i < text.length; i++) {
        const ch = text.charCodeAt(i);
        a = Math.imul(a ^ ch, 0x01000193) >>> 0;
        b = Math.imul(b + ch, 0x85ebca6b) >>> 0;
        c = (Math.imul(c ^ (ch + i), 0xc2b2ae35) ^ (a >>> 13)) >>> 0;
        d = (Math.imul(d + (ch * (i + 1)), 0x27d4eb2f) ^ (b >>> 11)) >>> 0;
    }
    return (a.toString(16).padStart(8, "0") +
        b.toString(16).padStart(8, "0") +
        c.toString(16).padStart(8, "0") +
        d.toString(16).padStart(8, "0"));
}
export function nextU32(h) {
    h.rngState = (h.rngState + 0x6d2b79f5) | 0;
    let t = h.rngState;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return (t ^ (t >>> 14)) >>> 0;
}
/** Uniform in [0, 1). */
export function nextFloat(h) {
    return nextU32(h) / 4294967296;
}
/** Uniform in [lo, hi). */
export function nextRange(h, lo, hi) {
    return lo + (hi - lo) * nextFloat(h);
}
/** Uniform integer in [lo, hiExclusive). Returns `lo` for an empty range. */
export function nextInt(h, lo, hiExclusive) {
    const span = hiExclusive - lo;
    if (span <= 0)
        return lo;
    return lo + Math.floor(nextFloat(h) * span);
}
/** A uniform point inside a disc of `radius`, as (dx, dz) offsets. */
export function nextPointInDisc(h, radius) {
    // sqrt on the radius keeps the distribution uniform by area rather than
    // clustering every shell at the centre.
    const r = radius * Math.sqrt(nextFloat(h));
    const a = nextFloat(h) * TWO_PI;
    return { dx: r * dcos(a), dz: r * dsin(a) };
}
/**
 * Fisher-Yates over a copy. Used for spawn assignment so that join order does
 * not decide where you land.
 */
export function shuffled(h, items) {
    const out = items.slice();
    for (let i = out.length - 1; i > 0; i--) {
        const j = nextInt(h, 0, i + 1);
        const a = out[i];
        const b = out[j];
        if (a === undefined || b === undefined)
            continue;
        out[i] = b;
        out[j] = a;
    }
    return out;
}
//# sourceMappingURL=rng.js.map